import React from "react";
import { Select, MenuItem, Checkbox, ListItemText, Divider } from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const SELECT_ALL_VALUE = "__select_all__";

const ACCENT = "#63c9dc";
const TEXT = "#280137";
const BORDER = "rgba(65, 60, 88, 0.12)";
const BORDER_HOVER = "rgba(99, 201, 220, 0.32)";
const BORDER_FOCUS = "rgba(99, 201, 220, 0.42)";
const PLACEHOLDER = "rgba(109, 95, 131, 0.76)";
const OPTION_HOVER = "rgba(99, 201, 220, 0.1)";
const OPTION_SELECTED = "rgba(99, 201, 220, 0.16)";

// Checkbox multi-select with a pinned "Select All" row and a collapsed
// "N tools selected" summary instead of chips, for the narrow Tools column
// in the Prompt Hub multi-agent table. `value` is filtered against `options`
// so a saved tool name that no longer exists in the API Integration list
// silently drops out instead of reappearing as a stray selection.
export const ToolsMultiSelectDropdown = ({
    id,
    value,
    onChange,
    options,
    placeholder = "Select tools",
    className,
}) => {
    const selected = (value || []).filter((tool) => options.includes(tool));
    const allSelected = options.length > 0 && selected.length === options.length;
    const isIndeterminate = selected.length > 0 && !allSelected;

    const handleChange = (event) => {
        const nextValue = event.target.value;
        if (nextValue.includes(SELECT_ALL_VALUE)) {
            onChange(allSelected ? [] : options);
            return;
        }
        onChange(options.filter((option) => nextValue.includes(option)));
    };

    return (
        <Select
            id={id}
            className={className}
            size="small"
            multiple
            displayEmpty
            value={selected}
            onChange={handleChange}
            IconComponent={KeyboardArrowDownOutlinedIcon}
            renderValue={(renderSelected) =>
                renderSelected.length === 0
                    ? placeholder
                    : `${renderSelected.length} tool${renderSelected.length === 1 ? "" : "s"} selected`
            }
            sx={{
                minHeight: 40,
                width: "100%",
                borderRadius: "14px",
                backgroundColor: "#ffffff",
                "& .MuiSelect-select": {
                    color: selected.length === 0 ? PLACEHOLDER : TEXT,
                    padding: ".5rem .75rem",
                },
                "& .MuiSvgIcon-root": {
                    color: "#6d5f83",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: BORDER,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: BORDER_HOVER,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: BORDER_FOCUS,
                    borderWidth: "1px",
                },
            }}
            MenuProps={{
                PaperProps: {
                    sx: {
                        backgroundColor: "#ffffff",
                        color: TEXT,
                        border: `1px solid ${BORDER}`,
                        borderRadius: "14px",
                        boxShadow: "0 18px 36px rgba(19, 11, 34, 0.12)",
                        maxHeight: 320,
                        marginTop: "4px",
                    },
                },
            }}
        >
            <MenuItem
                value={SELECT_ALL_VALUE}
                disabled={options.length === 0}
                sx={{
                    color: TEXT,
                    fontWeight: 600,
                    "&:hover": { backgroundColor: OPTION_HOVER },
                }}
            >
                <Checkbox
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    sx={{
                        color: "rgba(65, 60, 88, 0.35)",
                        "&.Mui-checked": { color: ACCENT },
                        "&.MuiCheckbox-indeterminate": { color: ACCENT },
                    }}
                />
                <ListItemText primary="Select All" />
            </MenuItem>
            <Divider sx={{ borderColor: BORDER }} />
            {options.length === 0 ? (
                <MenuItem disabled sx={{ color: PLACEHOLDER }}>
                    No API functions available
                </MenuItem>
            ) : (
                options.map((option) => (
                    <MenuItem
                        key={option}
                        value={option}
                        sx={{
                            color: `${TEXT} !important`,
                            "&:hover": { backgroundColor: OPTION_HOVER },
                            "&.Mui-selected": { backgroundColor: OPTION_SELECTED },
                            "&.Mui-selected:hover": { backgroundColor: OPTION_SELECTED },
                        }}
                    >
                        <Checkbox
                            checked={selected.includes(option)}
                            sx={{
                                color: "rgba(65, 60, 88, 0.35)",
                                "&.Mui-checked": { color: ACCENT },
                            }}
                        />
                        <ListItemText primary={option} />
                    </MenuItem>
                ))
            )}
        </Select>
    );
};
